/* eslint-disable import/no-default-export */
import { HeaderTestIds } from '@trello/webdriverio-tests';
import { IconName } from 'app/common/components/controls/icon';
import Backbone from 'app/common/vendor/backbone';
import { ChannelSwitcherPopoverId } from 'app/gamma/src/components/popovers/popover-ids';
import React, { lazy, Suspense, FunctionComponent, useRef } from 'react';
import { getMe } from 'app/gamma/src/selectors/members';
import { connect } from 'react-redux';

import { MemberModel } from 'app/gamma/src/types/models';
import HeaderButton from './button';
import { State } from 'app/gamma/src/modules/types';
import { usePopover } from '../popover';

const ChannelSwitcherPopoverPromise = import(/* webpackChunkName: "channel-switcher-popover" */ 'app/gamma/src/components/popovers/channel-switcher');
const ChannelSwitcherPopover = lazy(() => ChannelSwitcherPopoverPromise);

interface ChannelSwitcherButtonProps {
  member: MemberModel | undefined;
  hostname: string;
}

// Show channel picker if user is allowed to view multiple channels
// OR if we're not on production (to make development easier)
const shouldShowChannelPicker = (
  member: MemberModel | undefined,
  hostname: string,
): boolean => {
  if (hostname !== 'trello.com') {
    return true;
  }

  return !!(member && member.channels && member.channels.allowed.length > 1);
};

export const ChannelSwitcherButton: FunctionComponent<
  ChannelSwitcherButtonProps
> = ({ member, hostname }) => {
  const channelSwitcherButtonRef = useRef<HTMLButtonElement>(null);
  const { isPopoverShown, showPopover } = usePopover({
    name: ChannelSwitcherPopoverId,
  });

  const showButton = shouldShowChannelPicker(member, hostname);
  if (!showButton) {
    return null;
  }

  return (
    <>
      <HeaderButton
        icon={IconName.Gear}
        onClick={showPopover}
        ref={channelSwitcherButtonRef}
        testId={HeaderTestIds.ChannelPickerButton}
      />
      {member &&
        showButton &&
        isPopoverShown && (
          <Suspense fallback={null}>
            <ChannelSwitcherPopover
              target={() => channelSwitcherButtonRef.current}
            />
          </Suspense>
        )}
    </>
  );
};

const mapStateToProps = (state: State) => {
  return {
    member: getMe(state),
    hostname: Backbone.history.location.hostname,
  };
};

export default connect(mapStateToProps)(ChannelSwitcherButton);
